import { useEffect, useRef, useState } from "react";
import { Mic, Square, Check, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Browser SpeechRecognition-based pronunciation practice.
 * Compares normalized transcript to `expected` (Spanish).
 * Falls back gracefully if the API is unavailable.
 */
function normalize(s) {
  return (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[¡¿.,!?;:"'()]/g, "")
    .trim();
}

export default function VoicePractice({ targets = [] }) {
  const [supported, setSupported] = useState(true);
  const [listeningIdx, setListeningIdx] = useState(-1);
  const [results, setResults] = useState({}); // idx -> { transcript, ok }
  const recogRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    const r = new SR();
    r.lang = "es-ES";
    r.interimResults = false;
    r.maxAlternatives = 3;
    recogRef.current = r;
  }, []);

  const start = (idx, expected) => {
    if (!recogRef.current) return;
    const r = recogRef.current;
    setListeningIdx(idx);
    r.onresult = (e) => {
      const alts = Array.from(e.results[0]).map((a) => a.transcript);
      const norm = alts.map(normalize);
      const expNorm = normalize(expected);
      const ok = norm.some((t) => t === expNorm || t.includes(expNorm));
      setResults((prev) => ({ ...prev, [idx]: { transcript: alts[0], ok } }));
      setListeningIdx(-1);
    };
    r.onerror = (ev) => { toast.error(`Mic error: ${ev.error}`); setListeningIdx(-1); };
    r.onend = () => setListeningIdx((i) => (i === idx ? -1 : i));
    try { r.start(); } catch (e) { setListeningIdx(-1); }
  };

  const stop = () => { try { recogRef.current?.stop(); } catch {} };

  if (!targets?.length) return null;

  return (
    <section className="p-6 rounded-3xl bg-white border border-slate-200 space-y-3" data-testid="voice-practice">
      <div>
        <div className="chip mb-1" style={{background:"hsl(345 80% 96%)",color:"hsl(345 70% 30%)",borderColor:"hsl(345 60% 88%)"}}>Speaking</div>
        <h3 className="font-display text-lg font-semibold text-slate-900">Practice out loud</h3>
        {!supported && <p className="text-sm text-slate-500 mt-1">Your browser doesn't support voice input — try Chrome or Edge.</p>}
      </div>
      <ul className="grid sm:grid-cols-2 gap-3">
        {targets.map((t, i) => {
          const r = results[i];
          const listening = listeningIdx === i;
          return (
            <li key={i} className="p-4 rounded-2xl border border-slate-200 bg-slate-50/50" data-testid={`voice-target-${i}`}>
              <div className="font-display text-lg text-slate-900">{t.spanish}</div>
              <div className="text-xs text-slate-500">{t.english}</div>
              <div className="mt-3 flex items-center gap-2">
                {!listening ? (
                  <button data-testid={`voice-start-${i}`} onClick={() => start(i, t.spanish)} disabled={!supported}
                    className="px-4 py-2 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                    <Mic size={14} /> Speak
                  </button>
                ) : (
                  <button data-testid={`voice-stop-${i}`} onClick={stop}
                    className="px-4 py-2 rounded-full bg-slate-800 text-white text-sm font-semibold flex items-center gap-2">
                    <Square size={14} /> Stop
                  </button>
                )}
                {r && (
                  <span data-testid={`voice-result-${i}`} className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${r.ok ? "bg-teal-50 text-teal-800" : "bg-amber-50 text-amber-800"}`}>
                    {r.ok ? <><Check size={12} /> Nice!</> : <><X size={12} /> Try again — heard "{r.transcript}"</>}
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
